import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { getApiBaseUrl, formatRelativeTime } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationsContext';
import CondoLayout from './CondoLayout';

const CATEGORIES = [
  { key: 'all', label: 'All' },
  { key: 'bulletins', label: 'Bulletins' },
  { key: 'tickets', label: 'Tickets' },
  { key: 'forum', label: 'Forum' },
];

const CATEGORY_STYLES = {
  bulletins: 'bg-notificationBulletins/20 text-notificationBulletins',
  tickets: 'bg-notificationTickets/20 text-notificationTickets',
  forum: 'bg-notificationForum/20 text-notificationForum',
};

const CATEGORY_LABELS = {
  bulletins: 'Bulletins',
  tickets: 'Tickets',
  forum: 'Forum',
};

export function NotificationsContent({ condo, condoSlug }) {
  const { getToken } = useAuth();
  const { markRead, markAllRead } = useNotifications();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    const fetchNotifications = async () => {
      if (!condo?._id) return;

      try {
        const response = await axios.get(
          `${getApiBaseUrl()}/api/notifications/condo/${condo._id}`,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        setNotifications(response.data.notifications || []);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch notifications:', err);
        setError('Failed to load notifications. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [condo?._id, getToken]);

  const visible = activeCategory === 'all'
    ? notifications
    : notifications.filter(n => n.category === activeCategory);

  const unreadCount = notifications.filter(n => !n.read).length;

  const handleNotificationClick = (notification) => {
    if (notification.read) return;
    setNotifications(current => current.map(
      n => (n._id === notification._id ? { ...n, read: true } : n)
    ));
    markRead(notification._id);
  };

  const handleMarkAllRead = async () => {
    if (unreadCount === 0) return;
    const previous = notifications;
    setNotifications(current => current.map(n => ({ ...n, read: true })));
    try {
      await markAllRead(condo._id);
    } catch (err) {
      setNotifications(previous);
      setError('Failed to mark notifications as read. Please try again later.');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        <p className="mt-4 text-muted-foreground">Loading notifications...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-destructive/20 border border-destructive text-destructive-foreground px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {CATEGORIES.map((category) => (
          <button
            key={category.key}
            onClick={() => setActiveCategory(category.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              activeCategory === category.key
                ? 'bg-primary text-primary-foreground'
                : 'bg-secondary/50 text-muted-foreground hover:text-foreground'
            }`}
          >
            {category.label}
          </button>
        ))}
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllRead}
            className="ml-auto px-3 py-1.5 rounded-lg text-sm font-medium text-primary hover:bg-muted transition-colors"
          >
            Mark all as read
          </button>
        )}
      </div>

      {visible.length === 0 && !error ? (
        <div className="text-center py-12 bg-card rounded-lg border border-border">
          <div className="mb-4 text-muted-foreground flex justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-16 h-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </div>
          <p className="text-foreground text-base md:text-lg font-semibold">No notifications yet</p>
        </div>
      ) : (
        <ul className="bg-card border border-border rounded-lg divide-y divide-border overflow-hidden">
          {visible.map((notification) => (
            <li key={notification._id}>
              <Link
                to={`/${condoSlug}${notification.link}`}
                onClick={() => handleNotificationClick(notification)}
                className={`flex flex-col gap-1.5 px-4 py-3.5 hover:bg-muted transition-colors ${
                  notification.read ? '' : 'bg-primary/5'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className={`text-xs px-2 py-0.5 rounded ${CATEGORY_STYLES[notification.category]}`}>
                    {CATEGORY_LABELS[notification.category]}
                  </span>
                  <time
                    dateTime={notification.createdAt}
                    className="text-xs text-muted-foreground whitespace-nowrap"
                  >
                    {formatRelativeTime(notification.createdAt)}
                  </time>
                </div>
                <div className="flex items-start gap-2">
                  {!notification.read && (
                    <span
                      aria-label="Unread"
                      className="mt-1.5 shrink-0 w-2 h-2 rounded-full bg-notificationBadge"
                    />
                  )}
                  <p className={`text-sm text-foreground ${notification.read ? 'font-medium' : 'font-semibold'}`}>
                    {notification.title}
                  </p>
                </div>
                {notification.body && (
                  <p className="text-sm text-muted-foreground line-clamp-2">{notification.body}</p>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NotificationsPage() {
  return (
    <CondoLayout>
      {({ condo, condoSlug }) => <NotificationsContent condo={condo} condoSlug={condoSlug} />}
    </CondoLayout>
  );
}

export default NotificationsPage;
